import { http } from "./http";

export interface DoctorSummary {
  hospId: string;
  usrid: string;
  name: string;
  departmentId: string | null;
  departmentName: string | null;
  specialty: string | null;
  avatarUrl: string | null;
  isOnline: boolean;
  allowBooking?: boolean;
  rating: number;
  reviewCount: number;
  telemedFee: number;
  opdFee: number;
  yearsExp: number;
  todayStatus: "available" | "full" | "closed";
  weeklySchedule?: DoctorScheduleDay[];
}

export interface DoctorScheduleDay {
  label: string;
  date: string;
  status: "available" | "full" | "closed";
  hoursLabel: string | null;
}

export interface DoctorEducationView {
  degree: string;
  institution: string;
  year: number;
}

export interface DoctorReviewView {
  name: string;
  timeAgo: string;
  rating: number;
  text: string;
}

export interface DoctorProfileView {
  doctor: {
    id: string;
    hospId: string;
    usrid: string;
    fnameTh: string | null;
    lnameTh: string | null;
    fnameEn: string | null;
    lnameEn: string | null;
    name: string;
    departmentId: string | null;
    departmentName: string | null;
    specialty: string | null;
    licenseNo: string | null;
    avatarUrl: string | null;
    isOnline: boolean;
    allowBooking?: boolean;
    rating: number;
    reviewCount: number;
    yearsExp: number;
    casesHandled: number;
    telemedFee: number;
    opdFee: number;
    languages: string[];
  };
  about: string;
  specialties: string[];
  education: DoctorEducationView[];
  schedule: DoctorScheduleDay[];
  workingHours: string;
  topReview: DoctorReviewView | null;
}

// Phase 5.3 — per-doctor availability slot grid.
export type AvailabilityReason = "BOOKED" | "PAST" | "CLOSED" | "FULL";

export interface AvailabilitySlot {
  time: string; // "HH:mm" hospital local
  startsAt: string; // ISO UTC — pass back to book()
  available: boolean;
  reason?: AvailabilityReason;
}

export interface AvailabilityDay {
  date: string; // "YYYY-MM-DD" hospital local
  label: string; // single-char Thai day-of-week
  status: "available" | "full" | "closed";
  hoursLabel: string | null;
  slots: AvailabilitySlot[];
}

export interface AvailabilityView {
  departmentName?: string;
  doctorHospId: string;
  doctorUsrid: string;
  tzOffsetMin: number;
  days: AvailabilityDay[];
}

export const doctorApi = {
  async list(hospId: string, specialty?: string): Promise<DoctorSummary[]> {
    const params: Record<string, string> = { hospId };
    if (specialty) params.specialty = specialty;
    const { data } = await http.get<DoctorSummary[]>("/public/doctor", {
      params,
    });
    return data;
  },

  async getProfile(hospId: string, usrid: string): Promise<DoctorProfileView> {
    const { data } = await http.get<DoctorProfileView>(
      `/public/doctor/${hospId}/${usrid}`,
    );
    return data;
  },

  async availability(
    hospId: string,
    usrid: string,
    from?: string,
    days?: number,
  ): Promise<AvailabilityView> {
    const params: Record<string, string> = {};
    if (from) params.from = from;
    if (days) params.days = String(days);
    const { data } = await http.get<AvailabilityView>(
      `/public/doctor/${hospId}/${usrid}/availability`,
      { params },
    );
    return data;
  },

  // Phase 5.6 — customer review submission. Requires customer JWT.
  async submitReview(
    hospId: string,
    usrid: string,
    input: { appointmentId: string; rating: number; text?: string },
  ): Promise<{ id: string; pending: true }> {
    const { data } = await http.post<{ id: string; pending: true }>(
      `/public/doctor/${hospId}/${usrid}/review`,
      input,
    );
    return data;
  },
};
